/**
 * TelemetryView (P184) -- the live service-health feed: the twin's third
 * leg. A continuous, sampled signal (up / degraded / down + availability /
 * latency / error rate) distinct from discrete findings -- a service can
 * be telemetry-down with no finding, and that degrades its dependent IBSes
 * via the resilience deriver (P184.2).
 *
 * Synthetic generator today; a real push connector (Prometheus / Datadog /
 * OTel) is the production swap-in.
 */
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { Bdi } from "shared/components/Bdi";
import { useTranslation } from "react-i18next";

import { MarsadIcon } from "shared/components/MarsadIcon";
import { type ApiError } from "shared/api/client";
import type { ServiceHealth, TelemetryReport } from "shared/api/types";
import { useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";
import { formatDateTime } from "shared/time/formatDate";
import {
  useSampleTelemetry,
  useServiceHistory,
  useTelemetry,
} from "twin/api/telemetry_hooks";

import { ServiceHistoryChart } from "./ServiceHistoryChart";
import { normaliseSeries } from "./sparkline";

const STATUS_PILL: Record<string, string> = {
  down: "bg-rag-redBg text-rag-red",
  degraded: "bg-rag-amberBg text-rag-amber",
  healthy: "bg-rag-greenBg text-rag-green",
  unknown: "bg-slate-100 text-slate-500",
  // C5 -- never monitored. Grey like `unknown`, but a DIFFERENT word:
  // "unknown" here means a reading existed and went stale, which is a
  // gap in freshness. This is a gap in coverage, and the two want
  // different responses.
  no_data: "bg-slate-50 text-slate-400 border border-slate-200",
};

/**
 * ROUND 13 H, eighth round and half fixed -- the WORD above the
 * sentence.
 *
 * Twelve of twenty-seven rows read "healthy" while naming, in the same
 * row, the business service resting on them that carries an open
 * critical incident. The sentence beside the figure is right and was
 * the round-11 fix; the pill above it was left saying the opposite.
 *
 * The reading is NOT changed, and the reason is the one round 11 wrote
 * down: a tolerance breach on a business service is derived FROM live
 * conditions, so marking its supports degraded to explain it would
 * invert cause and effect. What changes is that "healthy" stops being
 * an unqualified verdict. It keeps its value and loses its green: a
 * word that means "this service's own telemetry is clean" must not
 * read as "all is well here" on a row that goes on to name an
 * incident.
 */
function StatusPill({
  status,
  contested = false,
  contestedBy = [],
}: {
  status: string;
  contested?: boolean;
  contestedBy?: readonly string[];
}) {
  const { t } = useTranslation();
  const softened = contested && status === "healthy";
  return (
    <span
      data-testid="telemetry-status"
      data-status={status}
      data-contested={softened ? "true" : undefined}
      title={
        softened
          ? t("modules.twin.telemetry.healthyButIncident", {
              names: contestedBy.join(", "),
            })
          : undefined
      }
      className={`rounded px-2 py-0.5 text-xs font-medium ${
        softened ? "bg-slate-100 text-slate-600" : (STATUS_PILL[status] ?? "")
      }`}
    >
      {softened
        ? t("modules.twin.telemetry.statusOwnTelemetry", {
            status: t(`modules.twin.telemetry.status.${status}`),
          })
        : t(`modules.twin.telemetry.status.${status}`)}
    </span>
  );
}

/** P227: a generic metric sparkline. "pct" metrics fill a fixed 0-100
 * band; "max" metrics (latency) fill relative to their own peak. */
function MetricSparkline({
  points,
  mode,
  tint,
  testid,
}: {
  points: number[];
  mode: "pct" | "max";
  tint: string;
  testid: string;
}) {
  const W = 64;
  const H = 16;
  const yfracs = normaliseSeries(points, mode);
  if (yfracs.length < 2) {
    return <span className="block text-[10px] text-slate-300">--</span>;
  }
  const step = W / (yfracs.length - 1);
  const path = yfracs
    .map((f, i) => `${(i * step).toFixed(1)},${(H - f * (H - 2) - 1).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      data-testid={testid}
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      aria-hidden="true"
      className="mt-0.5 block"
    >
      <polyline
        points={path}
        fill="none"
        strokeWidth="1.5"
        strokeLinejoin="round"
        className={tint}
      />
    </svg>
  );
}

/** P226: a compact "{n}m ago" / "{n}h ago" since the last observation,
 * for the stale-row note. */
export function formatAgo(observedAt: string, now: number = Date.now()): string {
  const mins = Math.max(0, Math.round((now - new Date(observedAt).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  return `${Math.round(mins / 60)}h`;
}

/** P227: the expanded per-service history chart row. */
function HistoryRow({ serviceId }: { serviceId: string }) {
  const { t } = useTranslation();
  const history = useServiceHistory(serviceId);
  return (
    <tr data-testid={`telemetry-history-${serviceId}`}>
      <td colSpan={5} className="bg-slate-50/60 px-3 py-2">
        {history.isLoading && (
          <p className="text-xs text-slate-400">
            {t("modules.twin.telemetry.history.loading")}
          </p>
        )}
        {history.isError && (
          <p className="text-xs text-rag-red">
            {t("modules.twin.telemetry.history.error")}
          </p>
        )}
        {history.data && <ServiceHistoryChart points={history.data.points} />}
      </td>
    </tr>
  );
}

function HealthRow({
  row,
  expanded,
  onToggle,
}: {
  row: ServiceHealth;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t, i18n } = useTranslation();
  return (
    <tr
      data-testid={`telemetry-row-${row.service_id}`}
      onClick={onToggle}
      className="cursor-pointer border-t border-slate-100 hover:bg-slate-50"
    >
      <td className="py-1.5 text-slate-800">
        <span aria-hidden="true" className="marsad-caret me-1 text-slate-400">
          {expanded ? "▾" : "▸"}
        </span>
        {/* C7 -- the row expanded history but never reached the
            service itself, so the reader who found a degraded service
            here had no way through to it. stopPropagation so the link
            does not also toggle the row it sits in. */}
        <Link
          to={`/twin/service/${row.service_id}`}
          data-testid={`telemetry-service-link-${row.service_id}`}
          onClick={(e) => e.stopPropagation()}
          className="text-brand-brassDark hover:underline"
        >
          <Bdi>{row.service_name}</Bdi>
        </Link>
        {row.source === "external" && (
          <span
            data-testid="telemetry-live"
            title={t("modules.twin.telemetry.liveSource")}
            className="ms-1.5 rounded bg-brand-brass/15 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand-brassDark"
          >
            {t("modules.twin.telemetry.live")}
          </span>
        )}
      </td>
      <td className="py-1.5">
        <StatusPill
          status={row.status}
          contested={(row.incident_ibs ?? []).length > 0}
          contestedBy={row.incident_ibs ?? []}
        />
        {/* Round 10 F6 -- when this reading was taken, on every row
            rather than only on a stale one. A monitoring page whose
            rows carry no time cannot be asked how current it is, and
            the answer was already on the wire. Four rounds. */}
        {row.is_stale ? (
          <span
            data-testid="telemetry-stale"
            className="ms-1.5 block text-[10px] text-slate-400"
          >
            {t("modules.twin.telemetry.staleNote", {
              ago: formatAgo(row.observed_at ?? ""),
            })}
          </span>
        ) : row.observed_at ? (
          <span
            data-testid={`telemetry-observed-${row.service_id}`}
            className="ms-1.5 block text-[10px] text-slate-400"
          >
            {t("modules.twin.telemetry.observedNote", {
              ago: formatAgo(row.observed_at),
            })}
          </span>
        ) : null}
      </td>
      <td className="py-1.5 tabular-nums text-slate-600">
        {row.availability_pct === null ? "--" : `${row.availability_pct}%`}
        {/* Round 11 F2 -- sixth round of a service reading "healthy
            99.88%" during an incident the reader has just read about.
            The two earlier fixes floor the reading when the critical
            finding is on the SERVICE or on a technology asset it runs
            on; in this estate the loudest incidents are recorded on
            the BUSINESS SERVICES above it, and this page lists
            services.

            Deliberately a sentence beside the figure rather than a
            change to it. The reading is not wrong: a tolerance breach
            on an IBS is derived FROM live conditions, so marking its
            supports degraded to explain it would invert cause and
            effect. What was missing was the context that made a true
            number look false. */}
        {row.own_alert_severity && (
          <span
            data-testid={`telemetry-own-alert-${row.service_id}`}
            className="mt-0.5 block text-[10px] font-semibold text-rag-red"
          >
            {t(
              row.own_alert_on_host
                ? "modules.twin.telemetry.ownAlertHost"
                : "modules.twin.telemetry.ownAlert",
              {
                severity: t(`board.severity.${row.own_alert_severity}`),
                since: formatDateTime(row.own_alert_since, i18n.language),
              },
            )}
          </span>
        )}
        {(row.incident_ibs ?? []).length > 0 && (
          <span
            data-testid={`telemetry-incident-${row.service_id}`}
            className="mt-0.5 block text-[10px] font-medium text-amber-800"
          >
            {t("modules.twin.telemetry.supportsIncident", {
              count: (row.incident_ibs ?? []).length,
              names: (row.incident_ibs ?? []).join(", "),
            })}
          </span>
        )}
        <MetricSparkline
          points={row.recent ?? []}
          mode="pct"
          tint="stroke-rag-green"
          testid="telemetry-spark"
        />
      </td>
      <td className="py-1.5 tabular-nums text-slate-600">
        {row.latency_ms === null ? "--" : `${row.latency_ms} ms`}
        <MetricSparkline
          points={row.recent_latency ?? []}
          mode="max"
          tint="stroke-brand-brassDark"
          testid="telemetry-spark-latency"
        />
      </td>
      <td className="py-1.5 tabular-nums text-slate-600">
        {row.error_rate_pct === null ? "--" : `${row.error_rate_pct}%`}
        <MetricSparkline
          points={row.recent_error ?? []}
          mode="pct"
          tint="stroke-rag-red"
          testid="telemetry-spark-error"
        />
      </td>
    </tr>
  );
}

/** Round 20 U8 -- CRM Service read down for over an hour and nobody was
 *  told; the page said only that telemetry is not a finding. It now
 *  says who is told about a sustained outage and after how long -- or,
 *  where nobody holds the seats, that nobody is, and how to tell them. */
function OutageNotice({ report }: { report: TelemetryReport }) {
  const { t } = useTranslation();
  const minutes = report.outage_notify_minutes;
  if (minutes == null) return null;
  const holders = report.outage_notify_opsres_holders ?? 0;
  const admins = report.outage_notify_admins ?? 0;
  const nobody = holders === 0 && admins === 0;
  return (
    <p
      data-testid="telemetry-outage-notice"
      data-nobody={nobody ? "true" : "false"}
      className="max-w-3xl text-sm text-slate-600"
    >
      {nobody ? (
        <>
          {t("modules.twin.telemetry.outageNobody", {
            minutes,
            seat: t("vocab.role.head_of_opsres"),
          })}{" "}
          <Link
            to="/twin/findings/new"
            className="text-brand-brassDark hover:underline"
          >
            {t("modules.twin.telemetry.outageRaise")}
          </Link>
        </>
      ) : (
        t("modules.twin.telemetry.outageTold", {
          minutes,
          seat: t("vocab.role.head_of_opsres"),
          holders,
          admins,
        })
      )}
    </p>
  );
}

export function TelemetryView(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const telemetry = useTelemetry();
  const sample = useSampleTelemetry();
  const isAdmin = me.data?.is_admin ?? false;
  const rows = telemetry.data?.rows ?? [];
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const onSample = () => {
    sample.mutate(undefined, {
      onSuccess: (r) =>
        toast.push({
          tone: "success",
          message: t("modules.twin.telemetry.sampled", {
            sampled: r.sampled,
            down: r.down,
            degraded: r.degraded,
          }),
        }),
      onError: (e: ApiError) => toast.push({ tone: "error", message: e.message }),
    });
  };

  return (
    <section data-testid="telemetry-view" className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
            {t("modules.twin.telemetry.kicker")}
          </p>
          <h1 className="font-display text-2xl text-slate-900">
            <MarsadIcon
              name="telemetry"
              size={22}
              className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
            />
            {t("modules.twin.telemetry.heading")}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            {t("modules.twin.telemetry.intro")}
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            data-testid="telemetry-sample"
            onClick={onSample}
            disabled={sample.isPending}
            className="shrink-0 rounded-md bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-60"
          >
            {sample.isPending
              ? t("modules.twin.telemetry.sampling")
              : t("modules.twin.telemetry.sampleNow")}
          </button>
        )}
      </header>

      {telemetry.isLoading && (
        <p data-testid="telemetry-loading" className="text-sm text-slate-500">
          {t("modules.twin.telemetry.loading")}
        </p>
      )}
      {telemetry.isError && (
        <p data-testid="telemetry-error" className="text-sm text-rag-red">
          {t("modules.twin.telemetry.error")}
        </p>
      )}

      {telemetry.data && <OutageNotice report={telemetry.data} />}

      {telemetry.data && (
        <>
          <div data-testid="telemetry-summary" className="flex flex-wrap gap-2 text-sm">
            {/* Round 17 B11 -- a count takes its colour only when there
                is something in it: "0 healthy" was green with nothing
                reporting. */}
            <span
              className={`rounded px-2 py-0.5 font-medium ${summaryTone(telemetry.data.down, "bg-rag-redBg text-rag-red")}`}
            >
              {t("modules.twin.telemetry.downCount", { n: telemetry.data.down })}
            </span>
            <span
              className={`rounded px-2 py-0.5 font-medium ${summaryTone(telemetry.data.degraded, "bg-rag-amberBg text-rag-amber")}`}
            >
              {t("modules.twin.telemetry.degradedCount", {
                n: telemetry.data.degraded,
              })}
            </span>
            <span
              className={`rounded px-2 py-0.5 font-medium ${summaryTone(telemetry.data.healthy, "bg-rag-greenBg text-rag-green")}`}
            >
              {t("modules.twin.telemetry.healthyCount", {
                n: telemetry.data.healthy,
              })}
            </span>
            {/* C5 -- how much of the estate nothing is watching. A
                monitoring page that lists only what it monitors
                cannot be asked the first question a supervisor asks
                of one. */}
            {(telemetry.data.no_data ?? 0) > 0 && (
              <span
                data-testid="telemetry-no-data-count"
                title={t("modules.twin.telemetry.noDataWhy")}
                className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-medium text-slate-500"
              >
                {t("modules.twin.telemetry.noDataCount", {
                  n: telemetry.data.no_data,
                })}
              </span>
            )}
            {(telemetry.data.stale ?? 0) > 0 && (
              <span
                data-testid="telemetry-stale-count"
                className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-500"
              >
                {t("modules.twin.telemetry.staleCount", {
                  n: telemetry.data.stale,
                })}
              </span>
            )}
          </div>

          {rows.length === 0 ? (
            /* Round 12 A6 -- "sample now to populate the feed" was said
               to the Resilience Lead on a page with no such control:
               the button is admin-gated because the endpoint is. The
               sentence now says what fills the feed, and the control
               sits with it for the seat that holds it. */
            <div data-testid="telemetry-empty" className="text-sm text-slate-600">
              <p className="max-w-2xl">
                {t("modules.twin.telemetry.empty")}
                {!isAdmin && " " + t("modules.twin.telemetry.emptyAskAdmin")}
              </p>
              {isAdmin && (
                <button
                  type="button"
                  data-testid="telemetry-empty-sample"
                  onClick={onSample}
                  disabled={sample.isPending}
                  className="mt-2 rounded-md bg-brand-brass px-3 py-1.5 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-60"
                >
                  {sample.isPending
                    ? t("modules.twin.telemetry.sampling")
                    : t("modules.twin.telemetry.sampleNow")}
                </button>
              )}
            </div>
          ) : (
            <article className="rounded-lg border border-slate-200 bg-white p-4">
              <table className="w-full text-start text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="py-1">{t("modules.twin.telemetry.colService")}</th>
                    <th className="py-1">{t("modules.twin.telemetry.colStatus")}</th>
                    <th className="py-1">
                      {t("modules.twin.telemetry.colAvailability")}
                    </th>
                    <th className="py-1">{t("modules.twin.telemetry.colLatency")}</th>
                    <th className="py-1">{t("modules.twin.telemetry.colErrorRate")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Fragment key={r.service_id}>
                      <HealthRow
                        row={r}
                        expanded={expandedId === r.service_id}
                        onToggle={() =>
                          setExpandedId((id) =>
                            id === r.service_id ? null : r.service_id,
                          )
                        }
                      />
                      {expandedId === r.service_id && (
                        <HistoryRow serviceId={r.service_id} />
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </article>
          )}
        </>
      )}
    </section>
  );
}

/** Round 17 B11 -- a zero count is drawn neutral, never in its RAG
 *  colour. */
function summaryTone(count: number, tone: string): string {
  return count === 0 ? "border border-slate-200 bg-white text-slate-500" : tone;
}
